import { useCallback, useMemo } from "react";
import type { EntityPoolConfig, EntitySelection } from "@salestrak/ui";
import { selectionsFromIds } from "#app/components/entity-selectors/utils";
import {
  USER_POOL_ID,
  useMemberItemResolver,
} from "#app/components/entity-selectors/hooks/use-user-pool";
import { TEAM_POOL_ID } from "#app/components/entity-selectors/hooks/use-team-pool";
import {
  useQuickFilterDirectory,
  type DirectoryKind,
} from "#app/lib/hooks/use-quick-filter-directory";

const NO_IDS: string[] = [];

/** The one directory kind every pool lists, or undefined when they mix or
 *  list something the directory cannot vouch for. */
function directoryKindOf(pools: readonly EntityPoolConfig[]): DirectoryKind | undefined {
  if (pools.length === 0) return undefined;
  if (pools.every((pool) => pool.id === USER_POOL_ID)) return "user";
  if (pools.every((pool) => pool.id === TEAM_POOL_ID)) return "team";
  return undefined;
}

/**
 * The selections for `ids` over `pools`, and `toIds` — the multi-select
 * write path back to an id list. `pools` must be memoized by the caller.
 *
 * The selections re-derive when the member directory answers: the member
 * pool's own `resolveItem` is deliberately stable, so a memo keyed on the
 * pool alone would never pick up a member past its loaded pages.
 *
 * An id no pool can show yet has no row to toggle, so `toIds` carries it
 * across the selector's change — dropping it would lose a pick that is only
 * slow to load. It is retired on that write once the server confirms it is
 * gone (`pruneUnknownIds`' rule), never while in flight, and never for a pool
 * the directory cannot vouch for. Clearing every row clears them too: an id
 * no row shows has no other way out.
 */
export function usePoolSelection(
  pools: readonly EntityPoolConfig[],
  ids: readonly string[],
) {
  const itemFor = useMemberItemResolver();
  const selections = useMemo(
    () =>
      selectionsFromIds(
        pools.map((pool) => (pool.id === USER_POOL_ID ? { ...pool, resolveItem: itemFor } : pool)),
        ids,
      ),
    [pools, ids, itemFor],
  );

  const unresolved = useMemo(() => {
    const shown = new Set(selections.map((selection) => selection.id));
    return ids.filter((id) => !shown.has(id));
  }, [ids, selections]);

  const kind = directoryKindOf(pools);
  const directory = useQuickFilterDirectory(kind === "team" ? unresolved : NO_IDS);

  const toIds = useCallback(
    (next: EntitySelection[]) => {
      if (next.length === 0) return [];
      const carried = kind
        ? unresolved.filter((id) => !directory.missingOf(id, kind))
        : unresolved;
      return [...new Set([...carried, ...next.map((selection) => selection.id)])];
    },
    [kind, unresolved, directory],
  );

  return { selections, toIds };
}
